import React, { FC, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { createUseStyles } from 'react-jss'
import moment from 'moment'
import { useAtom } from 'jotai'
import {
    Calendar,
    DeleteFill,
    IcDelete,
    Info,
    SendFill,
} from '../../../assets/icons'
import { Button } from '../../ui/Button/Button'
import {
    ICancelWorkplace,
    IRemoveMemberFromOwnList,
    Member,
} from '../../../models/Member'
import { CircleIcon, Circle } from '../../ui/Circle/Circle'
import { Typography } from '../../ui/Typography/Typography'
import { Overlay } from '../../ui/Sidebar'
import { DrawerHeaderMemberInfo } from '../FormElements/DrawerHeader'
import { FormHeader } from '../../ui/FormElememts/FormHeader'
import { FormControl } from '../../ui/FormElememts/FormControl'
import { FormTextArea } from '../FormElements/FormInput'
import { Workplace } from '../../../models/Workplace'
import { getWorkplacesAndPositions } from '../../../api/misc'
import { Position } from '../../../models/Position'
import { FormMessage } from '../../ui/FormElememts/FormMessage'
import { L, L_HTML } from '../../providers/Lokalise'
import { DatePickerComponent } from '../../ui/FormElememts/DatePicker'
import { colors } from '../../../theme/palette'
import { Select } from '../../ui/FormElememts/Select'

import { profileAtom } from '../../../api/profile/profile'
import {
    cancelMemberFromWorkplace,
    removeMemberFromOwnList,
} from '../../../api/members/members'
import { ImitatorNoAccessWarning } from '../Imitator/ImitatorNoAccessWarning'
import { Dialog } from '../../ui/Dialog/Dialog'

const useStyles = createUseStyles({
    contentWrapper: {
        padding: '100px 20px 20px',
        overflow: 'auto',
        height: 'calc(100% - 100px)',
        paddingBottom: '80px',
    },
    bottom: {
        display: 'flex',
        position: 'absolute',
        bottom: '0',
        width: '100%',
        padding: '20px',
        left: '0',
        background: colors.white,
        zIndex: 4,
        borderTop: `1px solid ${colors.borderColor}`,
        '& > button': {
            flexGrow: 1,
        },
        '& > button:last-of-type': {
            marginRight: '0',
        },
    },
})

interface RemoveMemberFromOwnListProps {
    member: Member
    onOpen?: () => void
}

export type RegistrationFormFields = {
    Arbejdsstednummer: string
    OevrigeOplysninger: string
}

export type CustomErrors = {
    Arbejdsstedsophorsdato?: boolean
}

const RemoveMemberFromOwnList: FC<RemoveMemberFromOwnListProps> = ({
    member,
    onOpen,
}) => {
    const [workPlaces, setWorkplaces] = useState<Workplace[]>([])
    const { register, formState, handleSubmit, setValue } =
        useForm<RegistrationFormFields>()
    const [selectedDate, _setSelectedDate] = useState(null)
    const [selectedWorkplace, setSelectedWorkplace] = useState<
        string | undefined
    >(undefined)
    const [selectedPosition, setSelectedPosition] = useState<
        string | undefined
    >(undefined)
    const [positions, setPositions] = useState<Position[]>([])
    const [isOpen, setIsOpen] = useState(false)
    const classes = useStyles()
    const { dirtyFields, errors } = formState
    const [customErrors, setCustomErrors] = useState<CustomErrors>({})
    const [reason, setReason] = useState<'skiftet' | 'stoppet'>('stoppet')
    const [authProfile] = useAtom(profileAtom)
    const [showImitatorModal, setShowImitatorModal] = useState(false)
    const [showDialog, setShowDialog] = useState(false)
    const defaultSystemText = L('FJERN_FRA_MEDLEMSLISTE_SYSTEM_BESKED_1')
    const setWP = (wp: string): void => {
        const found = workPlaces.find((wpp) => String(wpp.number) === wp)

        setSelectedWorkplace(wp)
        setSelectedPosition('default')

        if (found && found.positions) {
            setPositions(found.positions)
        }
    }

    const getPositions = async (): Promise<void> => {
        const response = await getWorkplacesAndPositions()

        let workplaces = response.workPlaces

        workplaces = workplaces.filter((place) => {
            return member.workPlaces.some(
                (work) => work.number !== place.number
            )
        })

        setWorkplaces(workplaces)
    }

    const resetForm = (): void => {
        setIsOpen(false)
    }

    const submitHandler = async (
        data: RegistrationFormFields
    ): Promise<string> => {
        if (authProfile.isImitator) {
            setShowImitatorModal(true)
            return Promise.resolve('OK')
        }

        if (reason === 'stoppet') {
            const fieldsToEdit: ICancelWorkplace = {
                Medlemsident: member.memberId,
                Arbejdsstednummer: Number(selectedWorkplace),
            }

            if (selectedDate) {
                fieldsToEdit.Arbejdsstedsophoersdato = moment(
                    selectedDate
                ).format('YYYY-MM-DD HH:mm:ss')
            }

            if (dirtyFields.OevrigeOplysninger) {
                fieldsToEdit.OevrigeOplysninger = data.OevrigeOplysninger
            }

            const response = await cancelMemberFromWorkplace(fieldsToEdit)
            if (response) {
                setIsOpen(false)
                setShowDialog(true)
                resetForm()
            }
            return Promise.resolve('OK')
        }

        const fields: IRemoveMemberFromOwnList = {
            Medlemsident: member.memberId,
            Arbejdsstednummer: Number(selectedWorkplace),
        }

        if (selectedDate) {
            fields.Aendringsdato = moment(selectedDate).format(
                'YYYY-MM-DD HH:mm:ss'
            )
        }

        let text = defaultSystemText

        if (dirtyFields.OevrigeOplysninger) {
            text = `${defaultSystemText}\n${data.OevrigeOplysninger}`
            fields.OevrigeOplysninger = String(text)
        } else {
            fields.OevrigeOplysninger = String(defaultSystemText)
        }

        const response = await removeMemberFromOwnList(fields)
        if (response) {
            setIsOpen(false)
            setShowDialog(true)
            resetForm()
        }
        return Promise.resolve('OK')
    }

    useEffect(() => {
        getPositions()
    }, [])

    return (
        <>
            <Button
                type="action"
                onClick={() => {
                    setIsOpen(true)
                    if (onOpen) {
                        onOpen()
                    }
                }}
                style={{
                    width: '100%',
                    display: 'flex',
                    alignContent: 'start',
                    padding: 0,
                    justifyContent: 'left',
                }}
            >
                <Circle marginRight=".5rem">
                    <CircleIcon iconWidth="22px">
                        <IcDelete />
                    </CircleIcon>
                </Circle>
                <Typography tag="h3" size="XS" spacing=".2rem">
                    {L('MINEMEDLEMMER_DETALJE_FJERN-FRA-MEDLEMSLISTE_KNAP')}
                </Typography>
            </Button>
            <Overlay
                isShowing={isOpen}
                hide={() => {
                    setIsOpen(false)
                    resetForm()
                }}
            >
                <DrawerHeaderMemberInfo member={member} />
                <form
                    noValidate
                    className={classes.contentWrapper}
                    onSubmit={handleSubmit(submitHandler)}
                >
                    <FormHeader>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                width: '100%',
                                marginBottom: '1rem',
                            }}
                        >
                            <div style={{ marginBottom: '1rem' }}>
                                <Circle marginRight=".5rem">
                                    <CircleIcon>
                                        <IcDelete />
                                    </CircleIcon>
                                </Circle>
                            </div>
                            <Typography tag="h3" size="M" spacing=".2rem">
                                {L('FJERN_FRA_MEDLEMSLISTE_OVERSKRIFT_LABEL')}
                            </Typography>
                        </div>
                    </FormHeader>
                    <FormControl>
                        <Select
                            required
                            name="Arbejdsstednummer"
                            hasError={!!errors?.Arbejdsstednummer?.message}
                            isOpen={isOpen}
                            placeholder="Arbejdssted *"
                            value={selectedWorkplace}
                            useControlled
                            onChange={(e) => {
                                setWP(e.target.value)
                                setValue('Arbejdsstednummer', e.target.value)
                            }}
                            options={member.workPlaces.map((wp) => {
                                const o = {
                                    label: wp.name,
                                    value: String(wp.number),
                                }
                                return o
                            })}
                        />
                        <FormMessage
                            text=""
                            error={errors?.Arbejdsstednummer?.message}
                        />
                    </FormControl>

                    <FormControl>
                        <Select
                            required
                            name="reason"
                            isOpen={isOpen}
                            value={reason}
                            placeholder="Årsag *"
                            onChange={(e) => {
                                setReason(
                                    e.target.value as 'skiftet' | 'stoppet'
                                )
                            }}
                            useControlled
                            options={[
                                {
                                    label: L(
                                        'STOPPET-PÅ-ARBEJDSPLADS_DROPDOWN_TEXT'
                                    ),
                                    value: 'stoppet',
                                },
                                {
                                    label: L('SKIFTET-STILLING_DROPDOWN_TEXT'),
                                    value: 'skiftet',
                                },
                            ]}
                        />
                    </FormControl>
                    <div style={{ marginTop: '3rem' }}>
                        <FormHeader>
                            <Circle marginRight=".5rem">
                                <CircleIcon iconWidth="18px">
                                    <Calendar />
                                </CircleIcon>
                            </Circle>
                            <Typography tag="h3" size="M" spacing=".2rem">
                                {L('FJERN_FRA_MEDLEMSLISTE_FORMHEADER_LABEL_2')}
                            </Typography>
                        </FormHeader>
                    </div>
                    <FormControl>
                        <DatePickerComponent
                            label={`${L('FJERN_MEDLEM_FORM_ÆNDRES-PR_LABEL')}`}
                            selected={selectedDate}
                            setSelected={(e) => {
                                _setSelectedDate(e)
                                setCustomErrors({})
                            }}
                            minDate={moment().subtract(1, 'year').toDate()}
                            maxDate={moment().add(1, 'year').toDate()}
                        />
                    </FormControl>
                    <div style={{ marginTop: '3rem' }}>
                        <FormHeader>
                            <Circle marginRight=".5rem">
                                <CircleIcon iconWidth="22px">
                                    <Info />
                                </CircleIcon>
                            </Circle>
                            <Typography tag="h3" size="M" spacing=".2rem">
                                {L('FJERN_FRA_MEDLEMSLISTE_FORMHEADER_LABEL_3')}
                            </Typography>
                        </FormHeader>
                    </div>
                    {reason === 'skiftet' ? (
                        <Typography
                            tag="h4"
                            size="XS"
                            spacing=".2rem"
                            style={{
                                fontStyle: 'italic',
                                marginBottom: '1rem',
                                fontWeight: 'bold',
                            }}
                        >
                            {defaultSystemText}
                        </Typography>
                    ) : null}

                    <FormControl>
                        <FormTextArea
                            label={L('GENERISK_FORM_ØVRIGE-OPLYSNINGER_LABEL')}
                            register={register}
                            name="OevrigeOplysninger"
                            required={false}
                        />
                    </FormControl>
                    <div className={classes.bottom}>
                        <Button
                            onClick={() => {
                                setIsOpen(false)
                                resetForm()
                            }}
                            type="secondary"
                            size="large"
                        >
                            Annuller
                        </Button>
                        <Button buttonType="submit" type="primary" size="large">
                            {L_HTML('MINEMEDLEMMER_FJERN_MEDLEM_FJERN_LABEL')}
                        </Button>
                    </div>
                </form>
            </Overlay>
            {showDialog ? (
                <Dialog
                    trigger={null}
                    defaultOpen
                    hideClose
                    icon={<SendFill width="46px" />}
                >
                    <Typography
                        style={{ marginBottom: '30px' }}
                        size="L"
                        align="center"
                    >
                        {reason === 'stoppet'
                            ? L_HTML('STOPPET-PÅ-ARBEJDSPLADS_POPUP_OVERSKRIFT')
                            : L_HTML('SKIFTET-STILLING_POPUP_OVERSKRIFT')}
                    </Typography>
                    <Typography
                        tag="p"
                        size="S"
                        style={{ marginBottom: '30px' }}
                        align="center"
                    >
                        {reason === 'stoppet'
                            ? L_HTML('STOPPET-PÅ-ARBEJDSPLADS_POPUP_TEXT')
                            : L_HTML('SKIFTET-STILLING_POPUP_TEXT')}
                    </Typography>
                    <Button size="xlarge" onClick={() => setShowDialog(false)}>
                        {reason === 'stoppet'
                            ? L_HTML('STOPPET-PÅ-ARBEJDSPLADS_POPUP_KNAP')
                            : L_HTML('SKIFT-ARBEJDSPLADS_POPUP_KNAP')}
                    </Button>
                </Dialog>
            ) : null}
            <ImitatorNoAccessWarning
                show={showImitatorModal}
                onClose={() => setShowImitatorModal(false)}
            />
        </>
    )
}

export { RemoveMemberFromOwnList }
